import React, { ChangeEvent, useRef } from 'react';
import { Form } from 'react-bootstrap';
import {
  Control,
  Controller,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { InputContainer } from '../styles';
import { moedaFormatada } from './formatMask';
import { IconContainer, InputIcon } from './styles';

interface InputTextProps extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  placeholder?: string;
  name: string;
  register: UseFormRegister<FieldValues>;
  isError: boolean;
  min: number;
  maxLength?: number;
  max?: number;
  decimals?: number;
  showIcon?: boolean;
  disabled?: boolean;
  control?: Control<FieldValues, any>;
  setValue?: UseFormSetValue<FieldValues>;
}

const MONEY_INPUT_REGEX = /^[0-9,.]+$/;

const isClearableValue = (value: string) => value === '' || value === '0,0';

const parseFormattedMoneyValue = (value: string) => {
  return Number.parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

interface FormatValueOptions {
  decimals: number;
  max: number;
  min: number;
  previousValue: string;
  fallbackValue?: string;
  validateMin: boolean;
}

const formatInputValue = (
  value: string,
  {
    decimals,
    max,
    min,
    previousValue,
    fallbackValue = previousValue,
    validateMin,
  }: FormatValueOptions,
) => {
  if (isClearableValue(value)) {
    return {
      nextPreviousValue: '',
      value: '',
    };
  }

  if (!MONEY_INPUT_REGEX.test(value)) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  const serializedValue = moedaFormatada(value, decimals);
  const parsedValue = parseFormattedMoneyValue(serializedValue);

  if (Number.isNaN(parsedValue)) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  if (validateMin && parsedValue <= min && parsedValue !== 0) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  if (parsedValue > max) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  return {
    nextPreviousValue: serializedValue,
    value: serializedValue,
  };
};

export const InputMoney: React.FC<InputTextProps> = ({
  label = '',
  placeholder = '',
  max = 999999999,
  min,
  name,
  register,
  isError,
  showIcon = true,
  disabled = false,
  control,
  decimals = 2,
  setValue = undefined,
  ...rest
}) => {
  const previousValueRef = useRef('');
  const registeredField = register(name);

  const applyFormattedValue = (
    event: ChangeEvent<HTMLInputElement>,
    {
      fallbackValue,
      validateMin,
    }: {
      fallbackValue?: string;
      validateMin: boolean;
    },
  ) => {
    const formattedResult = formatInputValue(event.target.value, {
      decimals,
      max,
      min,
      previousValue: previousValueRef.current,
      fallbackValue,
      validateMin,
    });

    previousValueRef.current = formattedResult.nextPreviousValue;
    event.target.value = formattedResult.value;

    return formattedResult.value;
  };

  const onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    applyFormattedValue(event, { validateMin: true });
    if (setValue !== undefined) setValue(name, event.target.value);
  };

  const getFormatedValue = (
    event: ChangeEvent<HTMLInputElement>,
    fallbackValue?: string,
  ) => {
    return applyFormattedValue(event, {
      fallbackValue,
      validateMin: false,
    });
  };

  return (
    <InputContainer>
      <Form.Group>
        {label && <Form.Label>{label}</Form.Label>}
        <InputIcon showIcon={showIcon}>
          <div style={{ position: 'relative' }}>
            {control ? (
              <Controller
                name={name}
                control={control}
                render={({ field: { value, onChange } }) => {
                  const currentValue = value ?? '';
                  const fallbackValue =
                    typeof currentValue === 'string'
                      ? currentValue
                      : String(currentValue);

                  previousValueRef.current = fallbackValue;

                  return (
                    <input
                      {...registeredField}
                      type="text"
                      className={
                        isError ? 'form-control is-invalid' : 'form-control'
                      }
                      value={currentValue}
                      placeholder={placeholder}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const formatedValue = getFormatedValue(
                          event,
                          fallbackValue,
                        );
                        onChange(formatedValue);
                      }}
                      disabled={disabled}
                      autoComplete="off"
                      {...rest}
                    />
                  );
                }}
              />
            ) : (
              <input
                {...registeredField}
                type="text"
                className={isError ? 'form-control is-invalid' : 'form-control'}
                placeholder={placeholder}
                onChange={onChangeValue}
                autoComplete="off"
                disabled={disabled}
                {...rest}
              />
            )}

            {showIcon && (
              <IconContainer className="container-money" disabled={disabled}>
                <i className="input-icon">R$</i>
              </IconContainer>
            )}
          </div>
        </InputIcon>
      </Form.Group>
    </InputContainer>
  );
};
